/**
 * Scientific notation the way it is printed: 7.9 × 10⁻⁶.
 *
 * Every number a student reads goes through here rather than through
 * toExponential ("7.9e-6", a programmer's spelling) or a caret ("×10^-6",
 * a typewriter's). One form, everywhere, and the one a textbook uses.
 */

const SUPERSCRIPT: Record<string, string> = {
  '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
}

/** An integer as superscript digits: -6 is "⁻⁶". */
export const superscript = (n: number): string => [...String(Math.round(n))].map((ch) => SUPERSCRIPT[ch] ?? ch).join('')

/** A bare power of ten: 6 is "10⁶". */
export const pow10 = (exp: number): string => `10${superscript(exp)}`

/** The drop-in for `value.toExponential(digits)`. */
export function sci(value: number, digits = 1): string {
  if (!Number.isFinite(value)) return Number.isNaN(value) ? '—' : value > 0 ? '∞' : '−∞'
  if (value === 0) return '0'
  const sign = value < 0 ? '−' : ''
  return sign + sciFromLog10(Math.log10(Math.abs(value)), digits)
}

/**
 * A value plotted as its log₁₀, read back for a hover readout: -5.1 is
 * "7.9 × 10⁻⁶", not "1e-5.1" - an exponent with a fraction in it is not a
 * number anybody writes.
 */
export function sciFromLog10(log: number, digits = 1): string {
  if (!Number.isFinite(log)) return '0'
  let exp = Math.floor(log)
  let mantissa = Number(Math.pow(10, log - exp).toFixed(digits))
  if (mantissa >= 10) {
    mantissa /= 10
    exp += 1
  }
  if (exp === 0) return mantissa.toFixed(digits)
  return `${mantissa.toFixed(digits)} × ${pow10(exp)}`
}

/**
 * The drop-in for `value.toPrecision(figures)`, which quietly switches to
 * "1.23e+4" once the value outgrows its figures or falls below 10⁻⁶.
 */
export function sig(value: number, figures: number): string {
  const text = value.toPrecision(figures)
  return text.includes('e') ? sci(value, figures - 1) : text
}
